import classNames from 'classnames';
import {getValidCoords} from 'is-valid-coords';
import React, {ReactElement, useCallback, useEffect, useState} from 'react';

import {Point} from '../../../query/graphql';
import {formatCoordinates} from '../../decision/util';
import {requestGeolocatePostcode, UK_POSTCODE_REGEX} from '../../util/requestGeolocatePostcode';
import SearchInput from '../SearchInput';

const POSTCODE_REGEXP = new RegExp(UK_POSTCODE_REGEX);

type Props = {
  postcode: string | undefined;
  location: Point | undefined;
  onChange: (postcode?: string, location?: Point) => void;
};

const LocationInput = ({postcode, location, onChange}: Props): ReactElement => {
  const [inputText, setInputText] = useState<string>(postcode || formatLocation(location) || '');
  const [error, setError] = useState<boolean>(false);

  const clearFiltersIfNeeded = useCallback(() => {
    if (postcode !== undefined || location !== undefined) {
      onChange(undefined, undefined);
    }
  }, [location, onChange, postcode]);

  const handleChangeText = useCallback(
    async (value: string) => {
      setError(false);
      setInputText(value);
      const sanitisedValue = value.trim();

      try {
        if (!sanitisedValue) {
          clearFiltersIfNeeded();
          return;
        }
        if (POSTCODE_REGEXP.test(sanitisedValue)) {
          const location = await requestGeolocatePostcode(sanitisedValue);
          onChange(sanitisedValue, {latitude: Number(location.lat), longitude: Number(location.lng)});
          return;
        }
        const coords = getValidCoords(sanitisedValue);
        if (coords) {
          onChange(undefined, {latitude: coords[0], longitude: coords[1]});
          return;
        }
        setError(true);
      } catch (e) {
        setError(true);
      }
    },
    [clearFiltersIfNeeded, onChange]
  );

  useEffect(() => {
    if (!postcode && !location) {
      setError(false);
      setInputText('');
    }
  }, [location, postcode]);

  return (
    <SearchInput
      placeholder={'Postcode or coordinates'}
      value={inputText}
      onChange={handleChangeText}
      className={classNames(error && 'error', (postcode || location) && 'border-primary-700')}
      containerClassName={'grow'}
    />
  );
};

export default LocationInput;

function formatLocation(location?: Point): string | undefined {
  return location ? formatCoordinates(location) : undefined;
}
